/**
 * The dealing date an order is stamped with at intake (spec 16.4, IP-36).
 *
 * The rule, receipt side only - clearance and discovery (inputs b and c) are
 * the cash slice's, and revival (a') the SLA slice's:
 *
 *   (a) the Asia/Colombo date of the receipt instant, moved on one day when
 *       the receipt is AFTER the fund's cut-off, then rolled forward to the
 *       first business day on or after it. The comparison is INCLUSIVE: a
 *       receipt at exactly 14:00:00.000 is before a 14:00 cut-off (E2), one
 *       millisecond later is after it (E3). A receipt on a non-business day
 *       rolls whatever its time (E4, E5).
 *   (r) v1 adds the investor's requested date (IP-36): LIV lets the investor
 *       pick a transaction date up to 14 days ahead, and dealing it earlier
 *       than instructed would be wrong. The date is the LATER of (a) and the
 *       first business day on or after requestedDate. A requested date in the
 *       past changes nothing.
 *
 * A subscription's date is PROVISIONAL until its money clears (the cash slice
 * re-stamps it); every other order type's is FINAL. The two legs of a switch
 * or transfer are each computed under their own fund's cut-off and then both
 * take the LATER date, the earlier leg recording input PAIR (spec 16.8).
 *
 * Asia/Colombo is UTC+05:30 all year (no daylight saving since 2006), so the
 * local date and time are plain arithmetic on the instant - no time-zone
 * library, nothing that depends on the Lambda's TZ.
 *
 * The calendar is data, never a weekday rule: one row per date with its
 * business-day flag (spec 15.3.4). A date outside the rows the caller loaded
 * is NOT a business day (the spec's is_business_day() says false outside the
 * horizon), and a search that runs off the end of the rows throws a
 * CalendarError instead of guessing - that is a configuration defect for a
 * person to fix, not an order fault.
 */

const COLOMBO_OFFSET_MS = 330 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const DATE = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const CUTOFF = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d)(?:\.(\d{1,6}))?)?$/;
const OFFSET_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

/** The zone every business date and cut-off is read in. */
export const TIME_ZONE = 'Asia/Colombo';

/** Which input decided the date (spec 16.2.3 dealing_date_input, plus v1's REQUESTED). */
export const DEALING_INPUT = Object.freeze({
  RECEIPT: 'RECEIPT',
  REQUESTED: 'REQUESTED',
  PAIR: 'PAIR',
});

/** A business-day question the loaded calendar cannot answer. */
export class CalendarError extends Error {
  constructor(message) {
    super(message);
    this.name = 'CalendarError';
  }
}

/**
 * True for a YYYY-MM-DD date that exists. The contract's date pattern admits
 * 2026-11-31 and 2026-02-30 (day 31 for any month), and Date quietly rolls
 * them into the next month; a round trip through Date refuses them.
 */
export function isCalendarDate(value) {
  if (typeof value !== 'string' || !DATE.test(value)) return false;
  return new Date(value + 'T00:00:00Z').toISOString().slice(0, 10) === value;
}

const isDate = isCalendarDate;

/** d + n calendar days, as YYYY-MM-DD. */
export function addDays(date, n) {
  if (!isDate(date)) throw new TypeError('not a YYYY-MM-DD date: ' + date);
  return new Date(Date.parse(date + 'T00:00:00Z') + n * DAY_MS).toISOString().slice(0, 10);
}

/**
 * d + n calendar months, as YYYY-MM-DD (IP-98). A day the target month lacks
 * clamps to its last day, exactly as PostgreSQL's date + interval 'n months'
 * does (devops/test/pg/schema-0011.test.js holds the two equal): 2028-02-29 +
 * 12 = 2029-02-28; 2025-08-31 + 6 = 2026-02-28; 2025-01-31 + 1 = 2025-02-28.
 */
export function addMonths(date, n) {
  if (!isDate(date)) throw new TypeError('not a YYYY-MM-DD date: ' + date);
  if (!Number.isSafeInteger(n)) throw new TypeError('months must be a whole number, not ' + String(n));
  const [y, m, d] = date.split('-').map(Number);
  const index = y * 12 + (m - 1) + n;
  const year = Math.floor(index / 12);
  const month = index - year * 12; // 0..11
  // Date.UTC reads a year below 100 as 1900 + it; no lot or fee date is one, but say so rather than guess.
  if (year < 100 || year > 9999) throw new RangeError(date + ' + ' + n + ' months is outside the years 100 to 9999');
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(d, last))).toISOString().slice(0, 10);
}

/**
 * An instant as milliseconds. A string must be RFC 3339 WITH an offset (or Z):
 * a timestamp without one would be read in the Lambda's own zone, which is the
 * bug this module exists to prevent.
 */
export function instantMs(value) {
  if (value instanceof Date) {
    const ms = value.getTime();
    if (Number.isNaN(ms)) throw new TypeError('an invalid Date');
    return ms;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && OFFSET_TIMESTAMP.test(value)) {
    const ms = Date.parse(value);
    if (!Number.isNaN(ms)) return ms;
  }
  throw new TypeError('not a time-zone-aware instant: ' + String(value));
}

/** The Asia/Colombo wall clock of an instant: its date and its milliseconds since local midnight. */
export function colomboClock(instant) {
  const local = instantMs(instant) + COLOMBO_OFFSET_MS;
  const date = new Date(local).toISOString().slice(0, 10);
  const msOfDay = ((local % DAY_MS) + DAY_MS) % DAY_MS;
  return { date, msOfDay };
}

/** The Asia/Colombo business date (YYYY-MM-DD) of an instant. */
export function colomboBusinessDate(instant) {
  return colomboClock(instant).date;
}

/** A cut-off 'HH:mm' or 'HH:mm:ss[.ffffff]' (a Postgres TIME) as milliseconds since midnight. */
export function cutoffMs(cutoffTime) {
  const match = typeof cutoffTime === 'string' ? CUTOFF.exec(cutoffTime.trim()) : null;
  if (!match) throw new TypeError('not a cut-off time (HH:mm or HH:mm:ss): ' + String(cutoffTime));
  const [, h, m, s = '0', fraction = ''] = match;
  const ms = Number((fraction + '000').slice(0, 3));
  return ((Number(h) * 60 + Number(m)) * 60 + Number(s)) * 1000 + ms;
}

/** True when the instant is strictly after the cut-off on its own Colombo date (inclusive rule). */
export function isAfterCutoff(instant, cutoffTime) {
  return colomboClock(instant).msOfDay > cutoffMs(cutoffTime);
}

/**
 * IP-191 (owner, 17 Oct 2026): whether a review at or before the cut-off is required for an
 * order to be dealt on its date (IP-125). It is not any more: book close freezes every
 * freezable order, reviewed or not, and nothing rolls for want of a review. Read by book close
 * (books/close.js) and by the cancel's freeze test (orders/ending.js), so the two agree. Here,
 * in a module that imports nothing, so neither depends on the other.
 */
export const REVIEW_REQUIRED_AT_CUTOFF = false;

/**
 * IP-125 (design 2.4): an order is REVIEWED IN TIME for a book when its review
 * cycle's first review ("order".first_reviewed_at) is at or before that
 * book's cut-off (order_book.cutoff_at). Only such an order is frozen into the
 * book; any other rolls to the next open dealing day (IP-126). A send-back
 * clears first_reviewed_at, so a cycle that ended never counts. Inclusive,
 * like a receipt against its cut-off (isAfterCutoff): a review at exactly the
 * cut-off is in time.
 *
 * @param {string|number|Date|null} firstReviewedAt
 * @param {string|number|Date} cutoffAt
 */
export function reviewedInTime(firstReviewedAt, cutoffAt) {
  if (firstReviewedAt === null || firstReviewedAt === undefined) return false;
  return instantMs(firstReviewedAt) <= instantMs(cutoffAt);
}

/**
 * The instant a date's cut-off falls, as ISO UTC: order_book.cutoff_at.
 * '2026-10-05' at '14:00' in Colombo is 2026-10-05T08:30:00.000Z.
 */
export function cutoffInstant(date, cutoffTime) {
  if (!isDate(date)) throw new TypeError('not a YYYY-MM-DD date: ' + date);
  return new Date(Date.parse(date + 'T00:00:00Z') + cutoffMs(cutoffTime) - COLOMBO_OFFSET_MS).toISOString();
}

function rowDate(row) {
  return row.date ?? row.cal_date ?? row.calDate;
}

function rowIsBusiness(row) {
  const flag = row.isBusinessDay ?? row.is_business_day;
  if (typeof flag === 'boolean') return flag;
  const type = row.dayType ?? row.day_type;
  if (typeof type === 'string') return type === 'BUSINESS';
  throw new TypeError('a calendar row needs isBusinessDay (or day_type) for ' + rowDate(row));
}

/**
 * A calendar over the loaded rows.
 *
 * Accepts what a caller naturally holds: rows as the table returns them
 * ({cal_date, is_business_day} or {date, isBusinessDay}, or a day_type), a
 * plain object or Map of date -> boolean, or an object that already has the
 * methods (returned unchanged).
 *
 * @returns {{ from: string, to: string, isBusinessDay: (d: string) => boolean,
 *   nextBusinessDay: (d: string) => string, addBusinessDays: (d: string, n: number) => string }}
 */
export function calendarFrom(input) {
  if (input && typeof input.isBusinessDay === 'function' && typeof input.nextBusinessDay === 'function'
    && typeof input.addBusinessDays === 'function') {
    return input;
  }
  const days = new Map();
  let entries;
  if (Array.isArray(input)) entries = input.map((row) => [rowDate(row), rowIsBusiness(row)]);
  else if (input instanceof Map) entries = [...input.entries()];
  else if (input && typeof input === 'object') entries = Object.entries(input);
  else throw new TypeError('a calendar needs rows, a Map or an object of date -> business day');
  for (const [date, business] of entries) {
    if (!isDate(date)) throw new TypeError('not a YYYY-MM-DD calendar date: ' + String(date));
    if (typeof business !== 'boolean') throw new TypeError('the business-day flag of ' + date + ' is not a boolean');
    days.set(date, business);
  }
  if (!days.size) throw new CalendarError('the calendar is empty');
  const sorted = [...days.keys()].sort();
  const from = sorted[0];
  const to = sorted[sorted.length - 1];

  const isBusinessDay = (date) => days.get(date) === true;

  /** The first business day on or after the date (inclusive). */
  function nextBusinessDay(date) {
    if (!isDate(date)) throw new TypeError('not a YYYY-MM-DD date: ' + String(date));
    // Before the loaded rows nothing is known: the caller loaded too little.
    if (date < from) throw new CalendarError(date + ' is before the loaded calendar (' + from + '..' + to + ')');
    let day = date;
    while (day <= to) {
      if (isBusinessDay(day)) return day;
      day = addDays(day, 1);
    }
    throw new CalendarError('no business day on or after ' + date + ' in the loaded calendar (' + from + '..' + to + ')');
  }

  /** The n-th business day strictly after the date. */
  function addBusinessDays(date, n) {
    let day = date;
    for (let i = 0; i < n; i++) day = nextBusinessDay(addDays(day, 1));
    return day;
  }

  return { from, to, isBusinessDay, nextBusinessDay, addBusinessDays };
}

/**
 * The receipt-side dealing date of one leg.
 *
 * @param {object} args
 * @param {string|number|Date} args.receivedAt the receipt instant (decision.decidedAt), time-zone aware
 * @param {string} args.cutoffTime the fund's cut-off, 'HH:mm' or 'HH:mm:ss', Asia/Colombo
 * @param {object} args.calendar anything calendarFrom() accepts
 * @param {string|null} [args.requestedDate] the investor's requested date, YYYY-MM-DD
 * @param {string} [args.orderType] SUB gives basis PROVISIONAL; anything else FINAL
 * @returns {{ dealingDate: string, basis: 'PROVISIONAL'|'FINAL', input: string, receivedBusinessDate: string,
 *   afterCutoff: boolean, receiptDealingDate: string, requestedDealingDate: string|null, cutoffTime: string }}
 */
export function dealingDate({ receivedAt, cutoffTime, calendar, requestedDate = null, orderType }) {
  const cal = calendarFrom(calendar);
  const { date: receivedBusinessDate } = colomboClock(receivedAt);
  const afterCutoff = isAfterCutoff(receivedAt, cutoffTime);
  const receiptDealingDate = cal.nextBusinessDay(afterCutoff ? addDays(receivedBusinessDate, 1) : receivedBusinessDate);
  let requestedDealingDate = null;
  if (requestedDate !== null && requestedDate !== undefined) {
    if (!isDate(requestedDate)) throw new TypeError('not a YYYY-MM-DD requested date: ' + String(requestedDate));
    // A requested date before receipt cannot move the date; only look it up when it could.
    if (requestedDate > receiptDealingDate) requestedDealingDate = cal.nextBusinessDay(requestedDate);
  }
  const byRequest = requestedDealingDate !== null && requestedDealingDate > receiptDealingDate;
  return {
    dealingDate: byRequest ? requestedDealingDate : receiptDealingDate,
    basis: orderType === 'SUB' ? 'PROVISIONAL' : 'FINAL',
    input: byRequest ? DEALING_INPUT.REQUESTED : DEALING_INPUT.RECEIPT,
    receivedBusinessDate,
    afterCutoff,
    receiptDealingDate,
    requestedDealingDate,
    cutoffTime,
  };
}

/**
 * The pair rule: both legs take the LATER of their two dates, and the leg
 * that moved records input PAIR. Returns new objects; equal dates change
 * nothing.
 */
export function pairDealingDates(outLeg, inLeg) {
  if (outLeg.dealingDate === inLeg.dealingDate) return [outLeg, inLeg];
  const later = outLeg.dealingDate > inLeg.dealingDate ? outLeg.dealingDate : inLeg.dealingDate;
  const moved = (leg) => (leg.dealingDate === later ? leg : { ...leg, dealingDate: later, input: DEALING_INPUT.PAIR });
  return [moved(outLeg), moved(inLeg)];
}
